import React from 'react';
import { Box, Button } from '@mui/material';

import { useWidgetFrame } from '../frameContext';

// A little rubber-stamp look: tiny tilted navy-to-blue pill pinned to the button's top-right corner,
// nudging over the label's top edge instead of sitting beside it, so the button barely widens.
const stampSx = {
  position: 'absolute',
  top: 2,
  right: 5,
  px: 0.5,
  borderRadius: '5px',
  fontSize: 8.5,
  fontWeight: 800,
  lineHeight: '13px',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: '#fff',
  // Verstaani blues: the CTA accent fading into the logo navy, same pair as the primary button.
  background: 'linear-gradient(135deg, #4299e1 0%, #1a365d 100%)',
  border: '1px solid #fff',
  boxShadow: '0 1px 3px rgba(26, 54, 93, 0.4)',
  transform: 'rotate(-8deg)',
  pointerEvents: 'none',
};

/**
 * Quick actions (design "Button row"): primary first, outlined rest, single line clipped
 * horizontally on desktop, wrapping on mobile. Right-aligned by default, like the legacy
 * landing page header; `align: 'left'` opts out. The frame draws the edit controls.
 * An action with `badge` (e.g. "Uutta!") gets a small tilted stamp in its top-right corner.
 */
export default function ButtonsRow({ actions = [], align = 'right' }) {
  const frame = useWidgetFrame();
  const edit = frame?.mode === 'edit';
  const mobile = Boolean(frame?.isMobile);
  return (
    <Box
      data-widget-align={align}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: !mobile && align === 'right' ? 'flex-end' : 'flex-start', gap: 1.25, minHeight: 48, overflow: mobile ? 'visible' : 'hidden', flexWrap: mobile ? 'wrap' : 'nowrap' }}
    >
      {actions.map((action, index) => (
        <Button
          key={action.key}
          variant={index === 0 ? 'contained' : 'outlined'}
          data-testid={`quick-action-${action.key}`}
          onClick={action.onClick}
          disabled={edit}
          startIcon={action.icon ? <action.icon /> : null}
          sx={{
            px: 2.5,
            py: 1.25,
            borderRadius: '10px',
            fontWeight: 500,
            fontSize: 14,
            whiteSpace: 'nowrap',
            flexShrink: 0,
            position: 'relative',
            ...(action.badge ? { pr: 3.25 } : {}),
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            ...(index === 0
              ? { bgcolor: 'secondary.main', color: 'white', boxShadow: '0 4px 12px rgba(49, 130, 206, 0.3)', '&:hover': { bgcolor: 'secondary.light' } }
              : { borderColor: 'divider', bgcolor: 'background.paper', color: 'text.primary', '&:hover': { borderColor: 'secondary.main', bgcolor: 'background.paper' } }),
          }}
        >
          {action.label}
          {action.badge ? (
            <Box component="span" data-testid={`quick-action-${action.key}-badge`} sx={stampSx}>
              {action.badge}
            </Box>
          ) : null}
        </Button>
      ))}
    </Box>
  );
}
